import { useUserData } from '@conteg/auth';
import { Alert, Button, Flex } from '@conteg/ui';
import { CUSTOM_AUTH_TOKEN } from 'config';
import { useTranslation } from 'react-i18next';
import { useCourierAuditMessagesQuery } from 'types/generated/graphql';

import { useDebounce } from '../../../utils/hooks/use-debounce';
import AuditViewFilterHeader from '../components/audit-view-filter-header';
import { AuditViewLayout } from '../components/audit-view-layout';
import { CompanySelect } from '../components/company-select';
import { CourierInput } from '../components/courier-input';
import { DateRangeFilterComponent } from '../components/date-range-filter';
import { EventTypeSelect } from '../components/event-type-select';
import { EventsTable } from '../components/events-table';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { useCourierFilters } from '../use-audit-filters';
import { getDefaultDateRange } from '../utils';

/**
 * Courier View
 * Primary filter: Courier Personal ID
 * Optional filters: Company, Date range
 */
export function CourierView() {
  const { t } = useTranslation();
  const [filters, setFilters] = useCourierFilters();

  // Debounce the courier personal ID for the query
  const debouncedCourierPersonalId = useDebounce(
    filters.courierPersonalId,
    500
  );

  const userData = useUserData(CUSTOM_AUTH_TOKEN);

  // Fetch audit messages for the courier
  const {
    data: { courierAuditMessages } = {},
    isLoading,
    isError,
    error,
  } = useCourierAuditMessagesQuery(
    {
      courierIdentifier: debouncedCourierPersonalId,
      from: filters.dateFrom?.toISOString(),
      to: filters.dateTo?.toISOString(),
      pageIndex: parseInt(filters.page) - 1,
      pageSize: DEFAULT_PAGE_SIZE,
      subjects: filters.eventTypes.length > 0 ? filters.eventTypes : undefined,
      // If the user is bound to a tenant (company), BFF should filter implicitly by that tenant.
      // Otherwise allow selecting company via the CompanySelect filter.
      tenantSubjectId: filters.tenantSubjectId || undefined,
      sortOrder: filters.sortOrder,
    },
    {
      // Only fetch when courier personal ID has at least 3 characters to avoid unnecessary API calls
      enabled:
        !!debouncedCourierPersonalId && debouncedCourierPersonalId.length >= 3,
    }
  );

  const events = courierAuditMessages?.items ?? [];
  const pagination = {
    currentPage: (courierAuditMessages?.pageIndex ?? 0) + 1,
    totalPages: courierAuditMessages?.totalPages ?? 0,
    totalItems: courierAuditMessages?.totalCount ?? 0,
    itemsPerPage: courierAuditMessages?.pageSize ?? 50,
  };

  const hasActiveFilters =
    !!filters.tenantSubjectId ||
    filters.eventTypes.length > 0 ||
    filters.dateFrom?.getTime() !== getDefaultDateRange().from.getTime() ||
    filters.dateTo?.getTime() !== getDefaultDateRange().to.getTime();

  return (
    <AuditViewLayout currentView="courier">
      <AuditViewFilterHeader
        title={t('Events.Tabs.Courier')}
        mainFilter={
          <CourierInput
            personalId={filters.courierPersonalId}
            onPersonalIdChange={(value) =>
              setFilters({ courierPersonalId: value })
            }
          />
        }
        filters={
          <>
            <DateRangeFilterComponent
              value={{ from: filters.dateFrom, to: filters.dateTo }}
              onChange={(dateRange) =>
                setFilters({
                  dateFrom: dateRange.from,
                  dateTo: dateRange.to,
                })
              }
            />
            {/* Show company select only when user is a provider */}
            {userData?.isProvider ? (
              <CompanySelect
                selectedTenantSubjectId={filters.tenantSubjectId}
                onSelect={(tenantSubjectId) =>
                  setFilters({ tenantSubjectId, page: null })
                }
              />
            ) : null}
            <EventTypeSelect
              selectedEventTypes={filters.eventTypes}
              onEventTypesChange={(eventTypes) =>
                setFilters({ eventTypes, page: null })
              }
              view="courier"
            />
          </>
        }
        clearFiltersButton={
          hasActiveFilters ? (
            <Button
              title={t('Events.Button.ClearFilters')}
              onClick={() => {
                setFilters((prev) => ({
                  ...prev,
                  tenantSubjectId: null,
                  eventTypes: null,
                  dateFrom: null,
                  dateTo: null,
                  page: null,
                }));
              }}
              variant="danger"
            />
          ) : undefined
        }
      />

      <Flex flexDirection="column" gap="2rem">
        {isError && (
          <Alert
            type="error"
            title={t('Error.GeneralErrorMessage')}
            message={t('Events.Error.LoadingData.Courier')}
            error={error}
          />
        )}

        <EventsTable
          events={events}
          loading={isLoading}
          pagination={pagination}
          onPageChange={(page) => setFilters({ page: page.toString() })}
          sortOrder={filters.sortOrder}
          onSortChange={(sortOrder) => setFilters({ sortOrder })}
          view="courier"
        />
      </Flex>
    </AuditViewLayout>
  );
}
